import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { styled } from "@mui/material/styles";
import { Alert, Skeleton, Stack } from "@mui/material";
import { fetchIndicatorApi } from "../../../../Utilities/Url";
import { utils, writeFile } from "xlsx"; // Ensure xlsx library is imported
import DownloadIcon from "@mui/icons-material/Download";
import { canExportToCSV } from "../config";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const SmaTable = () => {
  const [sortColumn, setSortColumn] = useState("symbols");
  const [sortDirection, setSortDirection] = useState("asc");
  const SMA = useSelector((state) => state.SmaData);
  const searchItem = useSelector((state) => state.SearchItems);
  const rowsPerPage = useSelector((state) => state.RowsPerPage);
  const page = useSelector((state) => state.SetPage);
  const SetError = useSelector((state) => state.SetError);
  const tableTickerName = useSelector((state) => state.TickerName);
  const SelectedDropdownOption = useSelector(
    (state) => state.SelectedDropdownOption
  );
  const setActiveButton = useSelector((state) => state.ActiveButton);
  const selectedExchange = useSelector((state) => state.SetExchange);
  const isLoading = useSelector((state) => state.isLoading);
  const authToken = useSelector((state) => state.authToken);
  const selectedAnalytics = useSelector((state) => state.selectedAnalytics);

  const dispatch = useDispatch();
  useEffect(() => {
    fetchIndicatorApi(
      selectedExchange,
      setActiveButton,
      SelectedDropdownOption,
      dispatch,
      authToken,
      selectedAnalytics
    );
  }, [selectedExchange, setActiveButton]);

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "#ebebeb",
      color: theme.palette.common.black,
      borderRight: `1px solid ${theme.palette.divider}`,
      textAlign: "center",
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
      padding: theme.spacing(1),
      borderRight: `1px solid ${theme.palette.divider}`,
      padding: "4px 8px",
    },
    textAlign: "right",
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:last-child td, &:last-child th": {
      border: 0,
      borderRight: `1px solid ${theme.palette.divider}`,
    },
    fontWeight: "normal",
  }));

  const rowsPerPageOptions = [5, 10, 25];

  const handleChangePage = (event, newPage) => {
    dispatch({ type: "SET_PAGE", payload: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch({
      type: "ROWS_PER_PAGE",
      payload: parseInt(event.target.value, 10),
    });
    dispatch({ type: "SET_PAGE", payload: 0 });
  };

  // const handleExportCSV = () => {
  //   if (!SMA) return;
  //   // Apply sorting and pagination
  //   let sortedFilteredData =     (tableTickerName && (tableTickerName[0] || tableTickerName))
  //   // Prepare data for CSV export
  //   const dataToExport = sortedFilteredData.map((item) => ({
  //     SYMBOLS: item.Ticker,
  //     SMA5: item.SMAResults[0]?.Value?.toFixed(2) || '',
  //     SMA8: item.SMAResults[1]?.Value?.toFixed(2) || '',
  //     SMA10: item.SMAResults[2]?.Value?.toFixed(2) || '',
  //     SMA20: item.SMAResults[3]?.Value?.toFixed(2) || '',
  //     SMA50: item.SMAResults[4]?.Value?.toFixed(2) || '',
  //     SMA100: item.SMAResults[5]?.Value?.toFixed(2) || '',
  //     SMA200: item.SMAResults[6]?.Value?.toFixed(2) || '',
  //     STRENGTH: (item.SMASummary.SummaryPercent?.toFixed(2) || '') + '%',
  //     OVERALL: (item.Overall.SummaryPercent?.toFixed(2) || '') + '%',
  //   }));
  //   debugger

  //   if (dataToExport.length === 0) {
  //     console.warn('No data to export.');
  //     return;
  //   }

  //   // Create worksheet from data
  //   const ws = utils.json_to_sheet(dataToExport, {
  //     header: [
  //       'SYMBOLS', 'SMA5', 'SMA8', 'SMA10', 'SMA20', 'SMA50', 'SMA100', 'SMA200', 'STRENGTH', 'OVERALL'
  //     ],
  //   });

  //   // Create a new workbook
  //   const wb = utils.book_new();
  //   utils.book_append_sheet(wb, ws, "SMATable");

  //   // Export the workbook as a CSV file
  //   writeFile(wb, "sma_table.csv"); // Save file as CSV
  // };

  const handleSort = (column) => {
    if (column === sortColumn) {
      setSortDirection((prevDirection) =>
        prevDirection === "asc" ? "desc" : "asc"
      );
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };
  const handleExportCSV = () => {
    // Create headers for the CSV file
    const headers = [
      "Ticker",
      "SMA5",
      "SMA8",
      "SMA10",
      "SMA20",
      "SMA50",
      "SMA100",
      "SMA200",
      "STRENGTH",
      "OVERALL",
    ];

    // Map data to rows for the CSV file
    const csvData = (
      tableTickerName &&
      (tableTickerName[0] || tableTickerName)
    ).map((item, index) => {
      // Check if SMAResults is valid
      const getSMAValue = (indicator) =>
        item.SMAResults && Array.isArray(item.SMAResults)
          ? item.SMAResults.find(
              (ind) => ind.Indicator === indicator
            )?.Value?.toFixed(2) || ""
          : "";

      const SMA5 = getSMAValue("SMA5");
      const SMA8 = getSMAValue("SMA8");
      const SMA10 = getSMAValue("SMA10");
      const SMA20 = getSMAValue("SMA20");
      const SMA50 = getSMAValue("SMA50");
      const SMA100 = getSMAValue("SMA100");
      const SMA200 = getSMAValue("SMA200");

      return [
        item.Ticker || "", // Handle case if Ticker is undefined
        SMA5,
        SMA8,
        SMA10,
        SMA20,
        SMA50,
        SMA100,
        SMA200,
        (item.SMASummary?.SummaryPercent || "") + "%", // Add '%' to STRENGTH
        (item.Overall?.SummaryPercent || "") + "%", // Add '%' to OVERALL
      ];
    });

    // Combine headers and data
    const data = [headers, ...csvData];

    // Convert CSV data to worksheet
    const ws = utils.aoa_to_sheet(data);

    // Create a new workbook and add the worksheet
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "SMAData");

    // Write the workbook to a file
    writeFile(wb, "SMAData.xlsx"); // Change the file name as needed
  };

  const handleFirstPageButtonClick = (event) => {
    handleChangePage(event, 0);
  };

  const handleLastPageButtonClick = (event, count) => {
    handleChangePage(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  const TablePaginationActions = (props) => {
    const { count, page, rowsPerPage, onPageChange } = props;

    return (
      <div style={{ flexShrink: 0, marginLeft: "2.5em" }}>
        <IconButton
          onClick={(event) => onPageChange(event, 0)}
          disabled={page === 0}
          aria-label="first page"
        >
          <FirstPageIcon />
        </IconButton>
        <IconButton
          onClick={(event) => onPageChange(event, page - 1)}
          disabled={page === 0}
          aria-label="previous page"
        >
          <KeyboardArrowLeft />
        </IconButton>
        <IconButton
          onClick={(event) => onPageChange(event, page + 1)}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="next page"
        >
          <KeyboardArrowRight />
        </IconButton>
        <IconButton
          onClick={(event) => handleLastPageButtonClick(event, count)}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="last page"
        >
          <LastPageIcon />
        </IconButton>
      </div>
    );
  };

  let filterTicker =
    (tableTickerName && (tableTickerName[0] || tableTickerName)).filter((ele) =>
      ele.Ticker.includes(searchItem[searchItem.length - 1])
    ) || [];

  return SetError === null ? (
    <div>
      {isLoading ? (
        <Stack spacing={1} padding={"5px"}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={200}
            animation="wave"
          />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={50}
            animation="wave"
          />
          <div style={{ display: "flex", flexDirection: "row", gap: "15px" }}>
            <Skeleton
              variant="rectangular"
              width={300}
              height={100}
              animation="wave"
            />
            <Skeleton
              variant="rectangular"
              width={300}
              height={100}
              animation="wave"
            />
          </div>
        </Stack>
      ) : (
        <TableContainer sx={{ maxHeight: 365 }}>
          {canExportToCSV && (
            <div
              style={{
                display: "flex",
                justifyContent: "end",
                padding: "6px",
                cursor: "pointer",
              }}
            >
              <DownloadIcon
                onClick={handleExportCSV}
                aria-label="export to CSV"
              />
            </div>
          )}
          <Table stickyHeader aria-label="customized table" size="small">
            <TableHead>
              <TableRow>
                <StyledTableCell sx={{ fontWeight: "bold" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "2px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSort("symbols")}
                  >
                    SYMBOLS
                    {sortColumn === "symbols" && sortDirection === "asc" ? (
                      <KeyboardArrowDownIcon />
                    ) : (
                      <KeyboardArrowUpIcon />
                    )}
                  </div>
                </StyledTableCell>
                <StyledTableCell sx={{ fontWeight: "bold" }}>
                  SMA5
                </StyledTableCell>
                <StyledTableCell sx={{ fontWeight: "bold" }}>
                  SMA8
                </StyledTableCell>
                <StyledTableCell sx={{ fontWeight: "bold" }}>
                  SMA10
                </StyledTableCell>
                <StyledTableCell sx={{ fontWeight: "bold" }}>
                  SMA20
                </StyledTableCell>
                <StyledTableCell sx={{ fontWeight: "bold" }}>
                  SMA50
                </StyledTableCell>
                <StyledTableCell sx={{ fontWeight: "bold" }}>
                  SMA100
                </StyledTableCell>
                <StyledTableCell sx={{ fontWeight: "bold" }}>
                  SMA200
                </StyledTableCell>
                <StyledTableCell sx={{ fontWeight: "bold" }}>
                  STRENGTH
                </StyledTableCell>
                <StyledTableCell sx={{ fontWeight: "bold" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "2px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSort("overall")}
                  >
                    OVERALL
                    {sortColumn === "overall" && sortDirection === "asc" ? (
                      <KeyboardArrowDownIcon />
                    ) : (
                      <KeyboardArrowUpIcon />
                    )}
                  </div>
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {searchItem.length !== 0 && filterTicker.length === 0 ? (
                <TableRow>
                  <StyledTableCell colSpan={10} align="center">
                    NO DATA FOUND
                  </StyledTableCell>
                </TableRow>
              ) : (
                (filterTicker.length === 0
                  ? tableTickerName && (tableTickerName[0] || tableTickerName)
                  : filterTicker
                )
                  ?.sort((a, b) => {
                    if (sortColumn === "symbols") {
                      return sortDirection === "asc"
                        ? a.Ticker.localeCompare(b.Ticker)
                        : b.Ticker.localeCompare(a.Ticker);
                    } else if (sortColumn === "overall") {
                      const comparison =
                        b.Overall.SummaryPercent - a.Overall.SummaryPercent;
                      return sortDirection === "asc" ? comparison : -comparison;
                    }
                    return 0;
                  })
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

                  .map((item, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell sx={{ textAlign: "left" }}>
                        {item.Ticker}
                      </StyledTableCell>
                      {item.SMAResults.map((smaResult, smaIndex) => (
                        <StyledTableCell key={smaIndex}>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              justifyContent: "space-between",
                              textAlign: "right",
                              marginLeft: "15px",
                            }}
                          >
                            <div
                              style={{
                                height: "10px",
                                width: "10px",
                                backgroundColor: getColor(smaResult.Indication),
                                borderRadius: "100%",
                              }}
                            ></div>
                            {smaResult.Value?.toFixed(2)}
                          </div>
                        </StyledTableCell>
                      ))}
                      <StyledTableCell>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                            textAlign: "right",
                            marginLeft: "15px",
                          }}
                        >
                          <div
                            style={{
                              height: "10px",
                              width: "10px",
                              backgroundColor: getColor(
                                item.SMASummary.Summary
                              ),
                              borderRadius: "100%",
                            }}
                          ></div>
                          {item.SMASummary.SummaryPercent?.toFixed(2)}%
                        </div>
                      </StyledTableCell>
                      <StyledTableCell>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginLeft: "15px",
                          }}
                        >
                          <div
                            style={{
                              height: "10px",
                              width: "10px",
                              backgroundColor: getColor(item.Overall.Summary),
                              borderRadius: "100%",
                            }}
                          ></div>
                          {item.Overall.SummaryPercent?.toFixed(2)}%
                        </div>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {!(searchItem.length !== 0 && filterTicker.length === 0) &&
        !isLoading && (
          <TablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            component="div"
            count={
              filterTicker.length === 0
                ? (tableTickerName && (tableTickerName[0] || tableTickerName))
                    .length || 0
                : filterTicker.length
            }
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={TablePaginationActions}
          />
        )}
    </div>
  ) : (
    <Alert severity="error">{SetError}</Alert>
  );
};

export default SmaTable;

const getColor = (indication) => {
  switch (indication) {
    case "StrongBuy":
      return "#43a047";
    case "Buy":
      return "#81c784";
    case "OverBought":
      return "#1b5e20";
    case "Sell":
      return "#ef5350";
    case "StrongSell":
      return "#d32f2f";
    case "OverSold":
      return "#b71c1c";
    case "Neutral":
      return "#cfd8dc";
    default:
      return "#000000";
  }
};
